import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient, { ApiError, toApiError } from './apiClient';

/*
 * Shared API calls. Pass URLs from config/apiEndpoints.js, e.g.
 *
 *   api.getAll(API.sender.requests.list, { status: 'assigned', page: 1 })
 *   api.getById(API.sender.requests, 'RB-1048')           // resource object + id
 *   api.create(API.sender.requests.create, body)
 *   api.update(API.sender.requests.update('RB-1048'), body)
 *   api.remove(API.admin.users.remove(userId))
 *
 * or build a typed helper once:  const requestsApi = createResource(API.sender.requests)
 * Every function accepts an options object: { params, signal, headers, timeout, onUploadProgress }.
 * Errors are always ApiError (status, code, message, fieldErrors).
 */

async function request(method, url, { data, params, ...config } = {}) {
  if (!url || typeof url !== 'string') throw new ApiError({ message: 'API endpoint is missing.', code: 'BAD_ENDPOINT' });
  try {
    const res = await apiClient.request({ method, url, data, params, ...config });
    return res.data;
  } catch (err) {
    throw toApiError(err);
  }
}

// Normalise list responses: plain arrays, { items }, { data }, { content } (Spring), plus paging fields.
export function toPage(payload) {
  if (Array.isArray(payload)) return { items: payload, total: payload.length, page: 1, pageSize: payload.length };
  const items = payload?.items ?? payload?.data ?? payload?.content ?? payload?.results ?? [];
  return {
    items: Array.isArray(items) ? items : [],
    total: payload?.total ?? payload?.totalElements ?? payload?.count ?? (Array.isArray(items) ? items.length : 0),
    page: payload?.page ?? (payload?.number !== undefined ? payload.number + 1 : 1),
    pageSize: payload?.pageSize ?? payload?.size ?? (Array.isArray(items) ? items.length : 0),
  };
}

const itemUrl = (target, id) => {
  if (typeof target === 'function') return target(id);
  if (target && typeof target.byId === 'function') return target.byId(id);
  if (typeof target === 'string') return `${target.replace(/\/+$/, '')}/${encodeURIComponent(String(id))}`;
  throw new ApiError({ message: 'API endpoint is missing.', code: 'BAD_ENDPOINT' });
};

// Drop empty filters so they aren't sent as ?status=&city=
const cleanParams = (params) =>
  params ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')) : undefined;

export const api = {
  request,
  get: (url, options = {}) => request('get', url, { ...options, params: cleanParams(options.params) }),
  getAll: async (url, params, options = {}) => toPage(await request('get', url, { ...options, params: cleanParams(params) })),
  getById: (target, id, options) => request('get', itemUrl(target, id), options),
  post: (url, body, options) => request('post', url, { ...options, data: body }),
  create: (url, body, options) => request('post', url, { ...options, data: body }),
  put: (url, body, options) => request('put', url, { ...options, data: body }),
  update: (url, body, options) => request('put', url, { ...options, data: body }),
  patch: (url, body, options) => request('patch', url, { ...options, data: body }),
  delete: (url, options) => request('delete', url, options),
  remove: (url, options) => request('delete', url, options),

  // Multipart upload. `files` is a File, FileList, array of Files, or a ready FormData.
  upload(url, files, { fields = {}, fieldName = 'file', onProgress, ...options } = {}) {
    let form = files;
    if (!(files instanceof FormData)) {
      form = new FormData();
      const list = files instanceof File || files instanceof Blob ? [files] : Array.from(files || []);
      list.forEach((f) => form.append(fieldName, f));
      Object.entries(fields).forEach(([k, v]) => v !== undefined && form.append(k, typeof v === 'object' ? JSON.stringify(v) : v));
    }
    return request('post', url, {
      ...options,
      data: form,
      onUploadProgress: onProgress ? (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0) : undefined,
    });
  },

  // Download a file (receipt, report export) and save it with a safe file name.
  async download(url, { filename = 'download', params, ...options } = {}) {
    const blob = await request('get', url, { ...options, params: cleanParams(params), responseType: 'blob' });
    const safeName = String(filename).replace(/[^\w.\- ]+/g, '_').slice(0, 120) || 'download';
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = safeName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  },
};

// CRUD helpers bound to a resource from apiEndpoints, e.g. createResource(API.admin.journeys).
export function createResource(endpoints) {
  return {
    getAll: (params, options) => api.getAll(endpoints.list, params, options),
    getById: (id, options) => api.getById(endpoints.byId, id, options),
    create: (body, options) => api.create(endpoints.create, body, options),
    update: (id, body, options) => api.update(endpoints.update(id), body, options),
    patch: (id, body, options) => api.patch(endpoints.update(id), body, options),
    remove: (id, options) => api.remove(endpoints.remove(id), options),
  };
}

/**
 * Load data in a component. Re-runs when `key` changes and cancels stale requests.
 *   const { data, loading, error, reload } = useApi((signal) => requestsApi.getAll({ status }, { signal }), status);
 */
export function useApi(fetcher, key, { enabled = true } = {}) {
  const [state, setState] = useState({ data: undefined, error: null, loading: enabled });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));
    fetcherRef.current(controller.signal)
      .then((data) => !controller.signal.aborted && setState({ data, error: null, loading: false }))
      .catch((error) => {
        if (!controller.signal.aborted && !error?.cancelled) setState((s) => ({ ...s, error: toApiError(error), loading: false }));
      });
    return () => controller.abort();
  }, [key, nonce, enabled]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, reload };
}

/**
 * Run a write (create/update/delete) with loading and error state.
 *   const save = useMutation((body) => requestsApi.create(body));
 *   await save.run(form); save.loading; save.error?.fieldErrors
 */
export function useMutation(mutate) {
  const [state, setState] = useState({ loading: false, error: null, data: undefined });
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);

  const run = useCallback(async (...args) => {
    setState({ loading: true, error: null, data: undefined });
    try {
      const data = await mutateRef.current(...args);
      if (mounted.current) setState({ loading: false, error: null, data });
      return data;
    } catch (err) {
      const error = toApiError(err);
      if (mounted.current) setState({ loading: false, error, data: undefined });
      throw error;
    }
  }, []);

  return { ...state, run };
}

export { ApiError };
export default api;
